import * as Sentry from "@sentry/react";
import Keycloak from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { ROUTER_BASENAME } from "../../environment.js";
import { AuthContext, tokenBelongsToGroup } from "./AuthContext.js";
import { buildSilentCheckSsoRedirectUri } from "./silentCheckSsoRedirectUri.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthWebProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    async function bootstrap() {
      try {
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = setIsAuthenticatedState;
        keycloak.onAuthLogout = setIsAuthenticatedState;
        keycloak.onAuthRefreshError = setIsAuthenticatedState;
        keycloak.onTokenExpired = setIsAuthenticatedState;
        keycloak.onAuthSuccess = setIsAuthenticatedState;
        keycloak.onAuthRefreshSuccess = setIsAuthenticatedState;
        await keycloak.init({
          onLoad: "check-sso",
          // keycloak-js >= 25 defaults this to false; set explicitly. Polling
          // the KC session-status iframe fails without third-party storage
          // access (Chrome / incognito / ITP), which cleared the token and
          // caused an infinite login loop (MEIS-12157 / MEIS-12771).
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: buildSilentCheckSsoRedirectUri(
            location.origin,
            ROUTER_BASENAME
          ),
          silentCheckSsoFallback: true,
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          tokenBelongsToGroup(keycloak.idTokenParsed, groupName),
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
